import {t} from './i18n.mjs';
import {haptic} from './haptics.mjs';

export const HOLD_MS = 1000;
// Never flip below the target: even a top-edge target must not put feedback under a finger.
export function pointIdPosition(rect, box, viewportWidth, viewportLeft = 0) {
  return {left: Math.max(viewportLeft + 8, Math.min(rect.left, viewportLeft + viewportWidth - box.width - 8)),
    top: rect.top - box.height - 8};
}
export function sharedPrefix(ids) {
  if (ids.length < 2 || new Set(ids).size < 2 || ids.some(id => !id)) return '';
  let prefix = ids[0];
  for (const id of ids) { while (!id.startsWith(prefix)) prefix = prefix.slice(0, -1); }
  const end = Math.max(prefix.lastIndexOf('*'), prefix.lastIndexOf('-')) + 1;
  return end >= 6 && ids.every(id => id.length > end) ? prefix.slice(0, end) : '';
}
// Preserve the operator and distinguishing ending; fit using actual Inter glyph widths.
export function middleId(id, width, measure, ids = []) {
  if (measure(id) <= width) return id;
  const match = id.match(/^[^*\-]+[*\-][^*\-]+/);
  const head = match?.[0] ?? id.slice(0, 4);
  let tail = Math.min(4, id.length - head.length - 1);
  while (tail < id.length - head.length - 1 && ids.some(other => other !== id && other.endsWith(id.slice(-tail)))) tail++;
  let result = head + '…' + id.slice(-tail);
  for (let n = tail + 1; n < id.length - head.length; n++) {
    const next = head + '…' + id.slice(-n);
    if (measure(next) > width) break;
    result = next;
  }
  return result;
}
// Hold-to-copy only: no tap toggle. The pill appears after a short delay (no flash while a scroll starts),
// fills with the accent colour for HOLD_MS, copies, and always disappears when the finger or button lifts.
export const SHOW_DELAY_MS = 120, MOVE_TOLERANCE_PX = 8, KEY_FEEDBACK_MS = 1500, SINGLE_LINE_MAX_PX = 40;
const COPY_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="8.5" y="8.5" width="11" height="11" rx="2.5"/><path d="M15.5 5.5v-.5a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7.5a2 2 0 0 0 2 2h.5"/></svg>';
const CHECK_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
const WARN_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 7.5v5.5M12 16.5v.5"/></svg>';
let active = null;
export function closePointId() { active?.close(); }
// One shared description and one persistent live region: dynamically inserted live regions are not announced reliably.
function shared(id, className, role) {
  let node = document.getElementById(id);
  if (!node) { node = document.createElement('span'); node.id = id; node.className = className; if (role) node.setAttribute('role', role); document.body.append(node); }
  return node;
}
export function announce(text) {
  const node = shared('point-id-status', 'sr-only', 'status');
  node.textContent = ''; setTimeout(() => { node.textContent = text; }, 30);
}
function pill(id) {
  const popup = document.createElement('div'); popup.className = 'point-id-popover'; popup.setAttribute('aria-hidden', 'true');
  // Two identical faces: the accent face is revealed left to right, so every glyph is either fully on the
  // neutral surface or fully on the accent, each with its own contrast-checked text colour.
  for (const face of ['base', 'fill']) {
    const row = document.createElement('span'); row.className = 'point-id-face is-' + face;
    const text = document.createElement('span'); text.className = 'point-id-text'; text.textContent = id;
    const status = document.createElement('span'); status.className = 'point-id-status';
    const icon = document.createElement('span'); icon.className = 'point-id-icon'; icon.innerHTML = COPY_ICON;
    const label = document.createElement('span'); label.className = 'point-id-label';
    status.append(icon, label); row.append(text, status); popup.append(row);
  }
  return popup;
}
export function bindPointId(button, id) {
  if (!button.addEventListener) return; // Lightweight view-model test DOM.
  let showTimer, holdTimer, keyTimer, popup = null, start = null, pointer = null;
  const truncated = () => button.dataset.truncated === 'true';
  const place = () => {
    const rect = button.getBoundingClientRect(), box = popup.getBoundingClientRect();
    const position = pointIdPosition(rect, box, globalThis.visualViewport?.width ?? innerWidth, globalThis.visualViewport?.offsetLeft ?? 0);
    popup.style.left = position.left + 'px'; popup.style.top = position.top + 'px';
  };
  const close = () => {
    clearTimeout(showTimer); clearTimeout(holdTimer); clearTimeout(keyTimer); showTimer = holdTimer = keyTimer = undefined;
    popup?.remove(); popup = null; start = pointer = null;
    document.removeEventListener('keydown', escape); document.removeEventListener('scroll', close, true);
    if (active?.button === button) active = null;
  };
  const escape = event => { if (event.key === 'Escape') close(); };
  const show = state => {
    if (!popup) {
      closePointId();
      popup = pill(id); popup.classList.add(state); document.body.append(popup); place();
      active = {button, close};
      document.addEventListener('keydown', escape); document.addEventListener('scroll', close, true);
    }
  };
  const finish = ok => {
    if (!popup) show('is-done');
    popup.classList.remove('is-holding'); popup.classList.add('is-done', ok ? 'is-copied' : 'is-failed');
    for (const face of popup.querySelectorAll('.point-id-face')) {
      face.querySelector('.point-id-icon').innerHTML = ok ? CHECK_ICON : WARN_ICON;
      face.querySelector('.point-id-label').textContent = t(ok ? 'copied' : 'copyFailedShort');
    }
    // The label widens the pill. Very long IDs keep one line: then the check alone confirms (the status region still speaks).
    if (popup.getBoundingClientRect().height > SINGLE_LINE_MAX_PX) popup.classList.add('is-tight');
    place();
    announce(t(ok ? 'idCopied' : 'idCopyFailed'));
  };
  const copy = async () => {
    let ok = true;
    try { await navigator.clipboard.writeText(id); haptic(); } catch { ok = false; }
    // The user may have lifted the finger while the clipboard promise was pending.
    if (popup || keyTimer) finish(ok); else announce(t(ok ? 'idCopied' : 'idCopyFailed'));
  };

  button.addEventListener('contextmenu', event => { if (truncated()) event.preventDefault(); });
  button.addEventListener('pointerdown', event => {
    if (event.button !== 0 || !truncated()) return;
    close(); start = [event.clientX, event.clientY]; pointer = event.pointerId;
    try { button.setPointerCapture(event.pointerId); } catch {}
    showTimer = setTimeout(() => { show('is-holding'); }, SHOW_DELAY_MS);
    holdTimer = setTimeout(() => { holdTimer = undefined; copy(); }, HOLD_MS);
  });
  button.addEventListener('pointermove', event => {
    if (start && event.pointerId === pointer && Math.hypot(event.clientX - start[0], event.clientY - start[1]) > MOVE_TOLERANCE_PX) close();
  });
  // Lifting always ends the gesture, before or after the copy.
  for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) button.addEventListener(type, event => { if (start && event.pointerId === pointer) close(); });
  // Keyboard: Enter or Space copies at once. A timed hold would be a timing requirement for keyboard users.
  button.addEventListener('keydown', event => {
    if (!truncated() || !['Enter', ' '].includes(event.key)) return;
    event.preventDefault(); if (event.repeat) return;
    close(); show('is-done'); keyTimer = setTimeout(close, KEY_FEEDBACK_MS); copy();
  });
  button.addEventListener('blur', () => { if (keyTimer) close(); });
}
export function fitPointIds(box) {
  const ruler = document.createElement('span');
  Object.assign(ruler.style, {position: 'fixed', visibility: 'hidden', whiteSpace: 'pre'});
  document.body.append(ruler);
  for (const button of box.querySelectorAll('.plug-point-id[data-id]')) {
    if (!button.clientWidth) continue;
    const style = getComputedStyle(button);
    for (const key of ['fontFamily', 'fontWeight', 'fontSize', 'fontVariantNumeric', 'fontFeatureSettings', 'letterSpacing']) ruler.style[key] = style[key];
    const ids = JSON.parse(button.dataset.ids), full = button.dataset.id;
    const value = middleId(full, button.clientWidth, text => { ruler.textContent = text; return ruler.getBoundingClientRect().width; }, ids);
    const truncated=value !== full;
    if(button.dataset.truncated !== String(truncated)) closePointId();
    button.dataset.truncated=String(truncated);
    button.classList.toggle('is-full', !truncated); button.classList.toggle('is-truncated', truncated);
    if(truncated) {
      button.setAttribute('role','button'); button.setAttribute('tabindex','0'); button.title = full;
      shared('point-id-hint', 'sr-only').textContent = t('copyIdHint'); button.setAttribute('aria-describedby','point-id-hint');
    } else { for(const key of ['role','tabindex','title','aria-describedby']) button.removeAttribute(key); }
    button.replaceChildren();
    const prefix = button.dataset.prefix;
    if (value === full && prefix) {
      const dim = document.createElement('span'); dim.className = 'point-id-prefix'; dim.textContent = prefix;
      button.append(dim, full.slice(prefix.length));
    } else button.textContent = value;
  }
  ruler.remove();
}
